// The onboarding review QUEUE: filter, scan, open.
//
// Reviewing is deliberately not done here. This page used to carry a 560px
// drawer holding the whole review — company data, people, every document, the
// edit facility and the decision — which meant a reviewer judged a company
// through a letterbox beside a list they were no longer reading. Opening a row
// now goes to the review workspace, and this page does one job well.

import {
  Alert,
  Chip,
  CircularProgress,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  onboardingApi,
  UNDER_REVIEW_FILTER,
  type AccountType,
  type ApplicationListEntry,
  type ApplicationStatusFilter,
} from '../../api/onboarding.api.js';
import { StatusBadge } from '../../components/StatusBadge.js';
import { APP_LOCALE } from '../../utils/locale.js';

import { APPLICATION_STATUSES, slaLabel, slaTone, statusFilterFromQuery } from './review-sla.js';

/** Every column of the table, for the loading and empty rows. */
const COLUMN_COUNT = 10;
const TYPE_OPTIONS: AccountType[] = ['PERSONAL', 'BUSINESS'];

/** Ten rows is one screen a reviewer can actually work through. */
const PAGE_SIZE = 10;

function SlaCell({ row }: { row: ApplicationListEntry }): JSX.Element {
  // Only an application waiting on a reviewer has SLA days: an approved
  // customer is never overdue (DEV-86).
  if (row.slaDays === null) {
    return <Typography color="text.secondary">—</Typography>;
  }
  return (
    <Chip
      label={slaLabel(row.slaDays)}
      color={slaTone(row.slaDays)}
      size="small"
      title={
        row.waitingSince !== null
          ? `Waiting since ${new Date(row.waitingSince).toLocaleString(APP_LOCALE)}`
          : undefined
      }
    />
  );
}

export function OnboardingApplicationsPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // The status filter lives in the address bar, so /kyc-queue can open this
  // page already filtered to "under review" and a filtered view can be shared.
  const statusFilter = statusFilterFromQuery(searchParams.get('status'));
  const setStatusFilter = (next: ApplicationStatusFilter | ''): void => {
    const params = new URLSearchParams(searchParams);
    if (next === '') params.delete('status');
    else params.set('status', next);
    setSearchParams(params, { replace: true });
  };
  const [typeFilter, setTypeFilter] = useState<AccountType | ''>('');
  const [countryFilter, setCountryFilter] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<'updated' | 'created'>('updated');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);

  // Typing a company name should not fire a query per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Any change to what is being asked for starts again at page 1; staying on
  // page 4 of a filter that now has one page shows an empty table.
  useEffect(() => {
    setPage(1);
  }, [statusFilter, typeFilter, countryFilter, debouncedSearch, sort, direction]);

  const queryFilters = useMemo(
    () => ({
      ...(statusFilter !== '' ? { status: statusFilter } : {}),
      ...(typeFilter !== '' ? { accountType: typeFilter } : {}),
      ...(countryFilter.trim() !== '' ? { country: countryFilter.trim() } : {}),
      ...(debouncedSearch !== '' ? { search: debouncedSearch } : {}),
      sort,
      direction,
      page,
      limit: PAGE_SIZE,
    }),
    [statusFilter, typeFilter, countryFilter, debouncedSearch, sort, direction, page],
  );

  const { data: applications, isFetching, error: listError } = useQuery({
    queryKey: ['onboarding-applications', queryFilters],
    queryFn: async () => onboardingApi.listApplications(queryFilters),
    // Keep the current page on screen while the next loads, so paging does not
    // flash an empty table.
    placeholderData: keepPreviousData,
  });

  const items: ApplicationListEntry[] = applications?.entries ?? [];
  const total = applications?.total ?? 0;
  const pageCount = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  const toggleSort = (column: 'updated' | 'created'): void => {
    if (sort === column) setDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSort(column);
      setDirection('desc');
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom>
        Onboarding Applications
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Search name or e-mail"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260, flexGrow: 1 }}
          />
          <TextField
            label="Country"
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            sx={{ minWidth: 120 }}
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as ApplicationStatusFilter | '')}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All</MenuItem>
            {/* Waiting on a reviewer: what the KYC Queue used to list (DEV-160). */}
            <MenuItem value={UNDER_REVIEW_FILTER}>Under review</MenuItem>
            {APPLICATION_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Account type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as AccountType | '')}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All</MenuItem>
            {TYPE_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Paper>

      {listError !== null && listError !== undefined ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load applications:{' '}
          {listError instanceof Error ? listError.message : 'unknown error'}
        </Alert>
      ) : null}

      <Paper>
        {/* Ten columns since Risk and SLA joined (DEV-160): scroll inside the card rather than spill out of it. */}
        <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Applicant</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Stage</TableCell>
              <TableCell>Application</TableCell>
              <TableCell>KYC</TableCell>
              <TableCell>Risk</TableCell>
              <TableCell>SLA</TableCell>
              <TableCell sortDirection={sort === 'created' ? direction : false}>
                <TableSortLabel
                  active={sort === 'created'}
                  direction={sort === 'created' ? direction : 'desc'}
                  onClick={() => toggleSort('created')}
                >
                  Started
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={sort === 'updated' ? direction : false}>
                <TableSortLabel
                  active={sort === 'updated'}
                  direction={sort === 'updated' ? direction : 'desc'}
                  onClick={() => toggleSort('updated')}
                >
                  Updated
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isFetching ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  <Typography color="text.secondary" py={2}>
                    {total === 0 && debouncedSearch === ''
                      ? 'No onboarding applications yet'
                      : 'No application matches these filters'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  // Reviewing an application is a page, not a side panel.
                  onClick={() => void navigate(`/onboarding-applications/${row.id}`)}
                >
                  <TableCell>
                    {/* The name is what a reviewer is looking for; the address
                        is how they confirm it is the right one. */}
                    <Typography>
                      {row.applicantName !== '' ? row.applicantName : row.email}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.applicantName !== '' ? row.email : row.customerId}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={row.accountType} size="small" />
                  </TableCell>
                  <TableCell>
                    {row.country === '' ? '—' : row.country}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      ({row.jurisdiction})
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <code>{row.stageId}</code>
                  </TableCell>
                  <TableCell>
                    <StatusBadge kind="onboarding" value={row.status} />
                  </TableCell>
                  <TableCell>
                    <StatusBadge kind="kyc" value={row.kycStatus} />
                  </TableCell>
                  <TableCell>
                    {/* The rating the customer header shows (DEV-41). */}
                    <StatusBadge kind="risk" value={row.riskRating} />
                  </TableCell>
                  <TableCell>
                    <SlaCell row={row} />
                  </TableCell>
                  <TableCell>{new Date(row.createdAt).toLocaleDateString(APP_LOCALE)}</TableCell>
                  <TableCell>{new Date(row.updatedAt).toLocaleString(APP_LOCALE)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </TableContainer>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ p: 2 }}
          spacing={2}
        >
          <Typography variant="caption" color="text.secondary">
            {total === 0
              ? 'No applications'
              : `${String((page - 1) * PAGE_SIZE + 1)}-${String(
                  Math.min(page * PAGE_SIZE, total),
                )} of ${String(total)}`}
          </Typography>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, next: number) => setPage(next)}
            size="small"
            showFirstButton
            showLastButton
          />
        </Stack>
      </Paper>
    </>
  );
}
